import { create } from 'zustand';
import {
  ChannelType,
  CompetitorCalcType,
  CompetitorType,
  ElasticityCurveApiResponse,
  ElasticityData,
  FilterIsEmpty,
  FiltersData,
  LocationType,
  MarketShareApiParams,
  MarketShareData,
  Preferences,
  ProdutoReferencia,
  ProductFilterOption,
  SelectData,
  ShareType,
} from './types';
import { VisaoGeralElasticidadeTableRowData } from '../VisaoGeralElasticidade/types';

type State = {
  produtoReferencia: FiltersData;
  produtosCompetidores: FiltersData;
  redes: FiltersData;
  localizacao: FiltersData;
  segmentos: FiltersData;
  subsegmentos: FiltersData;
  periodoSemanas: number;
  tipoLocalizacao: LocationType;
  tipoConcorrente: CompetitorType;
  calculoConcorrente: CompetitorCalcType;
  tipoCanal: ChannelType;
  tipoShare: ShareType;
  canal: string;
  showSubsegmentos: boolean;

  // Preferências
  preferences: Preferences;

  // Responses dos endpoints
  elasticityResponse: ElasticityData | null;
  marketShareResponse: MarketShareData | null;
  rawMarketShareApiResponse: ElasticityCurveApiResponse | null;

  /** null = concorrentes ocultos até o usuário escolher valor ou volume */
  competitorDisplayMode: ShareType | null;

  // Estados de loading
  loadingPage: boolean;
  loadingElasticity: boolean;
  loadingMarketShare: boolean;

  // Estado de resultado vazio
  isEmptyResponse: boolean;

  // Validação de filtros vazios
  filterIsEmpty: FilterIsEmpty;

  // Dados do produto referência para o card
  produtoReferenciaInfo: ProdutoReferencia | null;

  // Filtros salvos
  savedFilterSelectList: SelectData[];
  savedFilterResponseData: any[];
  showModalSavingFilters: boolean;
  showModalSpeakWithSpecialistElasticidadeCruzada: boolean;
  curvesPayload: MarketShareApiParams | null;
};

type Actions = {
  setState: <K extends keyof State>(name: K, data: State[K]) => void;
  setFilterValue: (
    filterName: keyof Pick<State, 'produtoReferencia' | 'produtosCompetidores' | 'redes' | 'localizacao' | 'segmentos' | 'subsegmentos'>,
    value: (string | number)[],
  ) => void;
  setFilterList: (
    filterName: keyof Pick<State, 'produtoReferencia' | 'produtosCompetidores' | 'redes' | 'localizacao' | 'segmentos' | 'subsegmentos'>,
    list: SelectData[],
  ) => void;
  setFilterCache: (
    filterName: keyof Pick<State, 'produtoReferencia' | 'produtosCompetidores' | 'redes' | 'localizacao' | 'segmentos' | 'subsegmentos'>,
    cache: SelectData[],
  ) => void;
  setDataResponse: (data: {
    elasticity: ElasticityData | null;
    marketShare: MarketShareData | null;
    rawMarketShareApiResponse: ElasticityCurveApiResponse | null;
    produtoReferenciaInfo: ProdutoReferencia | null;
  }) => void;
  setCompetitorDisplayMode: (mode: ShareType | null) => void;
  setLoadingPage: (loading: boolean) => void;
  setFilterIsEmpty: (filterName: keyof FilterIsEmpty, isEmpty: boolean) => void;
  validateFilters: () => boolean;
  setFiltersFromVisaoGeralElasticidade: (
    data: VisaoGeralElasticidadeTableRowData,
    filters?: { segmentation?: string; subSegmentation?: string; region?: string },
  ) => void;
  reset: () => void;
  clearFilters: () => void;
};

export const elasticidadeCruzadaInitialState: State = {
  produtoReferencia: { list: [], value: [], cache: [] },
  produtosCompetidores: { list: [], value: [], cache: [] },
  redes: { list: [], value: [], cache: [] },
  localizacao: { list: [], value: [], cache: [] },
  segmentos: { list: [], value: [], cache: [] },
  subsegmentos: { list: [], value: [], cache: [] },
  periodoSemanas: 12,
  tipoLocalizacao: 'TODO_TERRITORIO',
  tipoConcorrente: 'PRODUTO_ESPECIFICO',
  calculoConcorrente: null,
  tipoCanal: 'TODOS',
  tipoShare: 'VOLUME',
  canal: 'TODOS',
  showSubsegmentos: false,

  preferences: {
    canalDigital: true,
    canalFisico: true,
    tipoPreco: 'PRECO_PAGO',
  },

  elasticityResponse: null,
  marketShareResponse: null,
  rawMarketShareApiResponse: null,
  competitorDisplayMode: null,

  loadingPage: false,
  loadingElasticity: false,
  loadingMarketShare: false,

  isEmptyResponse: false,

  filterIsEmpty: {
    produtoReferencia: false,
    produtosCompetidores: false,
    redes: false,
    tipoLocalizacao: false,
    periodoSemanas: false,
  },

  produtoReferenciaInfo: null,
  savedFilterSelectList: [],
  savedFilterResponseData: [],
  showModalSavingFilters: false,
  showModalSpeakWithSpecialistElasticidadeCruzada: false,
  curvesPayload: null,
};

export const useElasticidadeCruzadaStore = create<State & Actions>((set, get) => ({
  ...elasticidadeCruzadaInitialState,

  setState: (name, data) => set({ [name]: data }),

  setFilterValue: (filterName, value) =>
    set(state => ({
      [filterName]: { ...state[filterName], value },
    })),

  setFilterList: (filterName, list) =>
    set(state => ({
      [filterName]: { ...state[filterName], list },
    })),

  setFilterCache: (filterName, cache) =>
    set(state => ({
      [filterName]: { ...state[filterName], cache },
    })),

  setDataResponse: data =>
    set({
      elasticityResponse: data.elasticity,
      marketShareResponse: data.marketShare,
      isEmptyResponse: data.elasticity === null && data.marketShare === null,
      competitorDisplayMode: null,
      rawMarketShareApiResponse: data.rawMarketShareApiResponse,
      produtoReferenciaInfo: data.produtoReferenciaInfo,
    }),

  setCompetitorDisplayMode: mode => set({ competitorDisplayMode: mode }),

  setLoadingPage: loading => set({ loadingPage: loading }),

  setFilterIsEmpty: (filterName, isEmpty) =>
    set(state => ({
      filterIsEmpty: { ...state.filterIsEmpty, [filterName]: isEmpty },
    })),

  validateFilters: () => {
    const state = get();

    const hasProdutoReferencia = (state.produtoReferencia.value?.length ?? 0) > 0;

    const hasConcorrentes = state.tipoConcorrente === 'TODOS' ? true : (state.produtosCompetidores.value?.length ?? 0) > 0;

    const hasCanal = state.tipoCanal === 'TODOS' || (state.segmentos.value?.length ?? 0) > 0;

    const hasLocalizacao = state.tipoLocalizacao === 'TODO_TERRITORIO' || (state.localizacao.value?.length ?? 0) > 0;

    const isValid = hasProdutoReferencia && hasConcorrentes && hasCanal && hasLocalizacao && state.periodoSemanas > 0;

    set({
      filterIsEmpty: {
        produtoReferencia: !hasProdutoReferencia,
        produtosCompetidores: !hasConcorrentes,
        redes: false, // Não usado mais
        tipoLocalizacao: !hasLocalizacao,
        periodoSemanas: state.periodoSemanas <= 0,
      },
    });

    return isValid;
  },
  setFiltersFromVisaoGeralElasticidade: (data: VisaoGeralElasticidadeTableRowData, filters) => {
    const referenceProductOption: ProductFilterOption = {
      label: `${data.referenceProductGtin} - ${data.referenceProductDescricao === null ? 'sem descrição' : data.referenceProductDescricao}`,
      value: data.referenceProductGtin,
      competitors: [
        {
          id: 0,
          competitorGtin: data.topCompetitorGtin,
          descricao: data.topCompetitorDescricao,
          mainCompetitor: true,
        },
      ],
    };

    const competitorOption: SelectData = {
      label: `${data.topCompetitorGtin} - ${data.topCompetitorDescricao === null ? 'sem descrição' : data.topCompetitorDescricao}`,
      value: data.topCompetitorGtin,
    };

    const segmentation = filters?.segmentation || '';
    const subSegmentation = filters?.subSegmentation || '';
    const region = filters?.region || '';

    const regionOption: SelectData | null = region
      ? {
          label: region,
          value: region,
        }
      : null;

    set({
      produtoReferencia: {
        list: [],
        value: [data.referenceProductGtin],
        cache: [referenceProductOption],
      },
      produtosCompetidores: {
        list: [],
        value: [data.topCompetitorGtin],
        cache: [competitorOption],
      },
      tipoConcorrente: 'PRODUTO_ESPECIFICO',
      tipoCanal: segmentation ? 'SEGMENTOS' : 'TODOS',
      segmentos: segmentation ? { list: [], value: [segmentation], cache: [] } : { list: [], value: [], cache: [] },
      subsegmentos: subSegmentation ? { list: [], value: [subSegmentation], cache: [] } : { list: [], value: [], cache: [] },
      showSubsegmentos: !!subSegmentation,
      tipoLocalizacao: region ? 'REGIAO' : 'TODO_TERRITORIO',
      localizacao: regionOption ? { list: [], value: [region], cache: [regionOption] } : { list: [], value: [], cache: [] },
      filterIsEmpty: {
        ...get().filterIsEmpty,
        produtoReferencia: false,
        produtosCompetidores: false,
        tipoLocalizacao: false,
      },
    });
  },

  reset: () => set(elasticidadeCruzadaInitialState),

  clearFilters: () =>
    set({
      produtoReferencia: { list: [], value: [], cache: [] },
      produtosCompetidores: { list: [], value: [], cache: [] },
      redes: { list: [], value: [], cache: [] },
      localizacao: { list: [], value: [], cache: [] },
      segmentos: { list: [], value: [], cache: [] },
      subsegmentos: { list: [], value: [], cache: [] },
      tipoLocalizacao: 'REGIAO',
      tipoConcorrente: 'PRODUTO_ESPECIFICO',
      calculoConcorrente: null,
      tipoCanal: 'SEGMENTOS',
      showSubsegmentos: false,
    }),
}));
